import { Menu, MenuItem } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { IntlContext } from 'context/LocalisationContext';
import { config } from 'config/auth';
import { TbIcon } from 'components/Figma';
import { getLanguages } from '../../i18n/localeConfig';
import { Button } from 'components/Button';

function AccountMenuLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-sm text-inherit no-underline hover:underline">
      {children}
    </a>
  );
}

export function AccountMenu() {
  const { logout } = useAuth0();

  const intl = useIntl();

  const localeCtx = useContext(IntlContext);

  const [anchorElm, setAnchorElm] = useState<Element>();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [showLanguages, setShowLanguages] = useState(false);

  const showLanguageSubMenu = (event: React.MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    setAnchorElm(event.currentTarget);
    setShowLanguages(true);
  };

  const MENU_LIST = [
    {
      id: 0,
      hide: false,
      children: (
        <AccountMenuLink href="http://account.tradebyte.com/">
          <FormattedMessage id="account_menu.profile" defaultMessage="My Profile" />
        </AccountMenuLink>
      ),
    },
    {
      id: 1,
      hide: false,
      children: (
        <>
          <FormattedMessage id="account_menu.language" defaultMessage="Language" />
          <TbIcon name="ChevronRight" size="sm" />
        </>
      ),

      onClick: showLanguageSubMenu,
    },
    {
      id: 2,
      hide: false,
      children: (
        <AccountMenuLink href={`https://www.tradebyte.com/${localeCtx.currentConfig.publicationCode}/privacy-policy/`}>
          <FormattedMessage id="account_menu.data_protection" defaultMessage="Data Protection" />
        </AccountMenuLink>
      ),
    },
    {
      id: 3,
      hide: false,
      children: (
        <AccountMenuLink href={`https://www.tradebyte.com/${localeCtx.currentConfig.publicationCode}/imprint/`}>
          <FormattedMessage id="account_menu.imprint" defaultMessage="Imprint" />
        </AccountMenuLink>
      ),
    },
    {
      id: 4,
      hide: false,
      children: (
        <AccountMenuLink href="https://tbone.trade-server.net/">
          <FormattedMessage id="account_menu.tb_one" defaultMessage="TB.One" />
        </AccountMenuLink>
      ),
    },
    {
      id: 5,
      hide: false,
      children: <AccountMenuLink href="/releasenotes">Release Notes</AccountMenuLink>,
    },
    {
      id: 6,
      name: intl.formatMessage({
        id: 'account_menu.log_out',
        defaultMessage: 'Log Out',
      }),
      onClick: () => {
        logout({ logoutParams: { returnTo: config.AUTH0_LOGOUT_URL } }).catch(e => {
          console.error(e);
        });
      },
    },
  ];
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setShowLanguages(false);
  };

  return (
    <>
      <div className="flex flex-col w-full items-center">
        <Button
          aria-controls="simple-menu"
          aria-haspopup="true"
          data-testid="account-menu-trigger"
          onClick={handleClick}
          icon="ProfileFilled"
          size="lg"
        />
      </div>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: { sx: { border: '1px solid #1a1a1a' } },
        }}
      >
        {MENU_LIST.map(elem => (
          <MenuItem onClick={elem.onClick} key={elem.id} hidden={elem.hide}>
            {elem.children ? elem.children : elem.name}
          </MenuItem>
        ))}
      </Menu>
      {showLanguages && (
        <Menu
          key="LanguageMenu"
          keepMounted
          open={showLanguages}
          onClose={handleClose}
          anchorReference="anchorEl"
          anchorEl={anchorElm}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: { sx: { border: '1px solid #1a1a1a' } },
          }}
        >
          {getLanguages().map(lang => (
            <MenuItem
              selected={lang.entry.localeCode === localeCtx.currentConfig.localeCode}
              disabled={lang.entry.localeCode === localeCtx.currentConfig.localeCode}
              className="primary-200"
              key={`lang_${lang.locale}`}
              onClick={() => {
                handleClose();
                localeCtx.setLocale(lang.locale);
              }}
            >
              <FormattedMessage id={lang.entry.title} defaultMessage={lang.entry.title} />
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
